import React, { useState, useEffect } from 'react';
import { getImageUrl } from '../utils/imageUtils';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Star, Check, X, RefreshCw, Trash2 } from 'lucide-react';
import { Button } from './ui/button';

export default function TestimonialsManager() {
  const { api } = useAuth();
  const [pendingTestimonials, setPendingTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState(null);

  useEffect(() => {
    fetchPendingTestimonials();
  }, []);

  const fetchPendingTestimonials = async () => {
    setLoading(true);
    try {
      const response = await api.get('/testimonials/pending/mine');
      setPendingTestimonials(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des témoignages');
    } finally {
      setLoading(false);
    }
  };

  const approveTestimonial = async (id) => {
    setProcessingId(id);
    try {
      await api.put(`/testimonials/${id}/approve`);
      toast.success('Témoignage approuvé et publié sur votre profil !');
      setPendingTestimonials(prev => prev.filter(t => t.id !== id));
    } catch (error) {
      toast.error('Erreur lors de l\'approbation');
    } finally {
      setProcessingId(null);
    }
  };

  const rejectTestimonial = async (id) => {
    setProcessingId(id);
    try {
      await api.put(`/testimonials/${id}/reject`);
      toast.success('Témoignage refusé');
      setPendingTestimonials(prev => prev.filter(t => t.id !== id));
    } catch (error) {
      toast.error('Erreur lors du refus');
    } finally {
      setProcessingId(null);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="w-8 h-8 border-2 border-gold/30 border-t-gold rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="testimonials-manager">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-serif text-lg text-gold flex items-center gap-2">
          <Star className="w-5 h-5" /> Témoignages en attente
          {pendingTestimonials.length > 0 && (
            <span className="ml-2 px-2 py-0.5 bg-gold text-dark text-xs font-bold rounded-full">
              {pendingTestimonials.length}
            </span>
          )}
        </h3>
        <Button
          onClick={fetchPendingTestimonials}
          variant="outline"
          size="sm"
          className="text-gray-400 border-gray-600"
        >
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>

      {pendingTestimonials.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <Star className="w-10 h-10 mx-auto mb-3 opacity-50" />
          <p>Aucun témoignage en attente de validation</p>
        </div>
      ) : (
        <div className="space-y-3">
          {pendingTestimonials.map((testimonial) => (
            <div 
              key={testimonial.id} 
              className="glass-card p-4 border-l-4 border-l-gold"
              data-testid="pending-testimonial"
            >
              <div className="flex items-start gap-3">
                <Link to={`/member/${testimonial.author_id}`}>
                  {testimonial.author_profile_picture ? (
                    <img 
                      src={getImageUrl(testimonial.author_profile_picture)}
                      alt={testimonial.author_pseudo}
                      className="w-12 h-12 rounded-full object-cover border-2 border-gold/30"
                    />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-gold/20 flex items-center justify-center text-gold font-semibold text-lg">
                      {testimonial.author_pseudo?.[0]?.toUpperCase() || '?'}
                    </div>
                  )}
                </Link>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <Link 
                      to={`/member/${testimonial.author_id}`}
                      className="font-semibold text-gold hover:underline"
                    >
                      {testimonial.author_pseudo}
                    </Link>
                    <span className="text-xs text-gray-500">
                      {formatDate(testimonial.created_at)}
                    </span>
                  </div>
                  <p className="text-gray-300 text-sm leading-relaxed mb-4 italic">
                    "{testimonial.content}"
                  </p>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => approveTestimonial(testimonial.id)}
                      disabled={processingId === testimonial.id}
                      className="btn-gold flex items-center gap-2 text-sm"
                      data-testid="approve-testimonial-btn"
                    >
                      <Check className="w-4 h-4" />
                      Publier
                    </Button>
                    <Button
                      onClick={() => rejectTestimonial(testimonial.id)}
                      disabled={processingId === testimonial.id}
                      variant="outline"
                      className="text-red-400 border-red-400/50 hover:bg-red-400/10 flex items-center gap-2 text-sm"
                      data-testid="reject-testimonial-btn"
                    >
                      <X className="w-4 h-4" />
                      Refuser
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
